import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Users,
  Package,
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Video,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  useTikTokFetch,
  AnalyticsOverview,
  ShopInfo,
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../hooks/useTikTokFetch';
import '../styles/dashboard.css';

export default function Dashboard() {
  const { get, getShopId } = useTikTokFetch();
  const shopId = getShopId();

  const { data: shop, isLoading: shopLoading } = useQuery<ShopInfo>({
    queryKey: ['shop', shopId],
    queryFn: () => get(`/api/shop/${shopId}`),
    enabled: !!shopId,
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics', shopId],
    queryFn: () => get(`/api/shop/${shopId}/analytics?days=30`),
    enabled: !!shopId,
  });

  const isLoading = shopLoading || analyticsLoading;

  // Mock chart data for demo
  const chartData = [
    { name: 'Mon', revenue: 4000, orders: 24 },
    { name: 'Tue', revenue: 3000, orders: 18 },
    { name: 'Wed', revenue: 5000, orders: 30 },
    { name: 'Thu', revenue: 4500, orders: 28 },
    { name: 'Fri', revenue: 6000, orders: 35 },
    { name: 'Sat', revenue: 7500, orders: 42 },
    { name: 'Sun', revenue: 5500, orders: 32 },
  ];

  if (!shopId) {
    return (
      <div className="dashboard-container">
        <div className="connect-card">
          <div className="connect-icon">
            <Video className="w-16 h-16 text-primary" />
          </div>
          <h2>Connect Your TikTok Shop</h2>
          <p>
            Connect your TikTok Shop to start tracking affiliate conversions
            and managing creators.
          </p>
          <a href="/auth/install" className="connect-button">
            Connect TikTok Shop
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="logo-section">
            <div className="logo-badge">
              <Video className="w-6 h-6" />
            </div>
            <div className="logo-text">
              <h1>TikTok Shop</h1>
              <p className="shop-name">{shop?.shop_name || 'Loading...'}</p>
            </div>
          </div>
        </div>
        <div className="header-right">
          <span className={`status-badge ${shop?.is_active ? 'active' : 'inactive'}`}>
            {shop?.is_active ? 'Connected' : 'Disconnected'}
          </span>
          <button className="refresh-button">
            <RefreshCw className="w-4 h-4" />
            Sync
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="stat-header">
            <div className="stat-icon revenue">
              <DollarSign className="w-5 h-5" />
            </div>
            <div className={`stat-trend ${(analytics?.revenue_trend || 0) >= 0 ? 'positive' : 'negative'}`}>
              {(analytics?.revenue_trend || 0) >= 0 ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              {formatPercentage(analytics?.revenue_trend || 0)}
            </div>
          </div>
          <div className="stat-value">
            {isLoading ? '...' : formatCurrency(analytics?.total_revenue || 0)}
          </div>
          <div className="stat-label">Total Revenue</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="stat-header">
            <div className="stat-icon orders">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div className={`stat-trend ${(analytics?.orders_trend || 0) >= 0 ? 'positive' : 'negative'}`}>
              {(analytics?.orders_trend || 0) >= 0 ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              {formatPercentage(analytics?.orders_trend || 0)}
            </div>
          </div>
          <div className="stat-value">
            {isLoading ? '...' : formatNumber(analytics?.total_orders || 0)}
          </div>
          <div className="stat-label">Total Orders</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="stat-header">
            <div className="stat-icon creators">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="stat-value">
            {isLoading ? '...' : formatNumber(analytics?.total_creators || 0)}
          </div>
          <div className="stat-label">Active Creators</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="stat-header">
            <div className="stat-icon products">
              <Package className="w-5 h-5" />
            </div>
          </div>
          <div className="stat-value">
            {isLoading ? '...' : formatNumber(analytics?.total_products || 0)}
          </div>
          <div className="stat-label">Total Products</div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="stat-header">
            <div className="stat-icon commission">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="stat-value">
            {isLoading ? '...' : formatCurrency(analytics?.total_commission || 0)}
          </div>
          <div className="stat-label">Total Commission</div>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <motion.div
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="chart-header">
            <h3>Revenue Overview</h3>
            <span className="chart-period">Last 7 days</span>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ff88" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#111827',
                    border: '1px solid #1f2937',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#00ff88"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          className="leaderboard-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="chart-header">
            <h3>Top Creators</h3>
          </div>
          <div className="leaderboard-list">
            {analytics?.top_creators?.length ? (
              analytics.top_creators.map((creator, index) => (
                <div key={creator.id} className="leaderboard-item">
                  <div className="leaderboard-rank">{index + 1}</div>
                  <div className="leaderboard-info">
                    <span className="leaderboard-name">{creator.name}</span>
                    <span className="leaderboard-orders">{creator.orders} orders</span>
                  </div>
                  <div className="leaderboard-value">
                    {formatCurrency(creator.revenue)}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <Users className="w-8 h-8 text-muted" />
                <p>No creator data yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Recent Orders */}
      <motion.div
        className="orders-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <div className="chart-header">
          <h3>Recent Orders</h3>
          <a href="/orders" className="view-all">View all</a>
        </div>
        <div className="orders-table">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.recent_orders?.length ? (
                analytics.recent_orders.slice(0, 5).map((order) => (
                  <tr key={order.id}>
                    <td className="order-id">{order.order_id}</td>
                    <td>
                      <span className={`status-pill ${order.status.toLowerCase()}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="order-amount">
                      {order.amount ? formatCurrency(order.amount) : '-'}
                    </td>
                    <td className="order-date">
                      {order.created_at
                        ? new Date(order.created_at).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="empty-row">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
